import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { UserRole, SubscriptionTier } from '@/types';
import { supabase } from '@/integrations/supabase/client'; // Importer supabase pour les futures mises à jour
import { useTranslation } from 'react-i18next'; // Importer pour traduire les toasts

// Recréer l'interface AdminUser ici (ou l'importer si définie dans @/types)
export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
  subscriptionTier: SubscriptionTier;
  isVerified: boolean;
  createdAt: Date;
  lastLoginAt?: Date;
  status: 'active' | 'suspended' | 'pending';
}

// Données mockées initiales (à remplacer par un appel API réel)
const MOCK_USERS: AdminUser[] = [
  {
    id: '101',
    email: 'sophia@example.com',
    displayName: 'Sophia Rose',
    role: 'escort',
    subscriptionTier: 'regular',
    isVerified: true,
    createdAt: new Date('2024-01-15'),
    lastLoginAt: new Date(Date.now() - 1000 * 60 * 60 * 2),
    status: 'active',
  },
  {
    id: '102',
    email: 'mia@example.com',
    displayName: 'Mia Milano',
    role: 'escort',
    subscriptionTier: 'teaser',
    isVerified: true,
    createdAt: new Date('2024-02-23'),
    lastLoginAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3),
    status: 'active',
  },
   {
    id: '201',
    email: 'john@example.com',
    displayName: 'John D.',
    role: 'client',
    subscriptionTier: 'vip',
    isVerified: true,
    createdAt: new Date('2023-11-10'),
    lastLoginAt: new Date(Date.now() - 1000 * 60 * 30),
    status: 'active',
  },
  {
    id: '202',
    email: 'michael@example.com',
    displayName: 'Michael S.',
    role: 'client',
    subscriptionTier: 'regular',
    isVerified: false,
    createdAt: new Date('2024-03-05'),
    lastLoginAt: new Date(Date.now() - 1000 * 60 * 60 * 12),
    status: 'active',
  },
  {
    id: '203',
    email: 'robert@example.com',
    displayName: 'Robert K.',
    role: 'client',
    subscriptionTier: 'free',
    isVerified: false,
    createdAt: new Date('2024-04-01'),
    status: 'pending',
  },
  {
    id: '204',
    email: 'james@example.com',
    displayName: 'James L.',
    role: 'client',
    subscriptionTier: 'teaser',
    isVerified: true,
    createdAt: new Date('2023-12-15'),
    lastLoginAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7),
    status: 'suspended',
  },
];

export const useAdminUsers = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { t } = useTranslation(); // Obtenir t pour les toasts

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // TODO: Remplacer par un appel API réel avec React Query
      // Exemple: const { data, error } = await supabase.from('users').select('...');
      // Pour l'instant, simuler un délai et utiliser les mocks
      await new Promise(resolve => setTimeout(resolve, 500)); // Simuler le délai réseau
      setUsers(MOCK_USERS);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch users';
      console.error('Error fetching users:', err);
      setError(errorMessage);
      // Traduire le toast d'erreur
      toast({ title: t('common.error') || 'Error', description: t('admin.error_loading_users') || errorMessage, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast, t]); // Ajouter t aux dépendances

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const updateUserStatus = useCallback(async (userId: string, newStatus: 'active' | 'suspended' | 'pending') => {
    // TODO: Implémenter l'appel API réel pour mettre à jour le statut utilisateur via Supabase
    // Exemple: const { error } = await supabase.from('users').update({ status: newStatus }).eq('id', userId);
    console.log(`Updating user ${userId} status to ${newStatus}`);
    
    // Simuler la mise à jour locale pour l'instant
    setUsers(currentUsers =>
      currentUsers.map(user =>
        user.id === userId ? { ...user, status: newStatus } : user
      )
    );
    
    // Traduire le toast de succès
    toast({ 
      title: t('common.success') || 'Success', 
      description: t('admin.user_status_updated', { userId, status: newStatus }) || `User ${userId} status updated to ${newStatus}.` 
    });
    // Gérer les erreurs de l'appel API ici
    // Rafraîchir les données si nécessaire (React Query gèrera cela automatiquement)
    return true; // Simuler succès
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast, t]); // Ajouter t aux dépendances

  return {
    users,
    isLoading,
    error,
    fetchUsers, // Pour recharger manuellement si besoin
    updateUserStatus,
  };
}; 